// La card de `crear_planilla`: vista previa + descarga —
// ADR-2026-09-08-el-agente-lee-y-escribe-planillas.
//
// Se auto-resuelve al montar (como las otras cards de mostrar): armar el archivo no cuesta
// créditos y no hay nada que confirmar. Lo que el usuario ve es la tabla y un botón; los
// bytes se generan recién al tocar Descargar, así una planilla que nadie baja no cuesta ni
// memoria.
//
// Botón partido, igual que el de la galería: el cuerpo baja el formato que pidió el agente
// (xlsx salvo que le hayan pedido csv) y el chevron ofrece el otro.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Download, Table2 } from 'lucide-react';
import { escribirXlsx } from '../../../utils/planilla/xlsx';
import {
  contarCeldas,
  csvDePlanilla,
  normalizarPlanillaCreada,
  resumenPlanillaParaAgente,
  type FormatoPlanilla,
} from './planilla';
import type { UseV2ChatReturn, V2ToolCard } from '../useV2Chat';

/** Filas de la vista previa. Es una muestra, no el archivo: para eso está la descarga. */
const FILAS_PREVIA = 8;
const COLUMNAS_PREVIA = 8;

function bajar(blob: Blob, nombre: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  // Diferido: revocar en el mismo tick cancela la descarga en algunos navegadores.
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 200);
}

export const CrearPlanillaCard: React.FC<{
  card: V2ToolCard;
  chat: UseV2ChatReturn;
}> = ({ card, chat }) => {
  const planilla = useMemo(() => normalizarPlanillaCreada(card.input), [card.input]);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const resueltoRef = useRef(false);

  useEffect(() => {
    if (!resueltoRef.current && card.status === 'pending') {
      resueltoRef.current = true;
      chat.resolveToolCard(card.id, {
        ...resumenPlanillaParaAgente(planilla),
        // El objeto entero viaja en la respuesta para el `[registro:]` del historial.
        planilla,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!menuAbierto) return;
    const alClick = (ev: MouseEvent | TouchEvent) => {
      if (!menuRef.current?.contains(ev.target as Node)) setMenuAbierto(false);
    };
    document.addEventListener('mousedown', alClick);
    document.addEventListener('touchstart', alClick);
    return () => {
      document.removeEventListener('mousedown', alClick);
      document.removeEventListener('touchstart', alClick);
    };
  }, [menuAbierto]);

  const descargar = (formato: FormatoPlanilla) => {
    setMenuAbierto(false);
    setError(null);
    try {
      const base = planilla.nombre.replace(/\.(xlsx|csv)$/i, '');
      if (formato === 'csv') {
        bajar(new Blob([csvDePlanilla(planilla)], { type: 'text/csv;charset=utf-8' }), `${base}.csv`);
      } else {
        const bytes = escribirXlsx(planilla.hojas);
        bajar(
          new Blob([bytes as unknown as BlobPart], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          }),
          `${base}.xlsx`,
        );
      }
    } catch (err) {
      console.warn('[CrearPlanilla] descarga', err);
      setError('No pude armar el archivo. Probá de nuevo.');
    }
  };

  const hoja = planilla.hojas[0];
  const filas = hoja?.filas ?? [];
  const previa = filas.slice(0, FILAS_PREVIA);
  const ocultas = Math.max(0, filas.length - previa.length);
  const celdas = contarCeldas(planilla.hojas);
  const otroFormato: FormatoPlanilla = planilla.formato === 'csv' ? 'xlsx' : 'csv';
  // Un csv es UNA tabla: con varias hojas, avisamos que sólo va la primera.
  const avisoCsv = planilla.hojas.length > 1;

  return (
    <div className="rounded-2xl border border-[rgb(var(--brand-black-rgb)_/_0.08)] bg-white p-3">
      <div className="flex items-start justify-between gap-2 px-1 pb-2">
        <span className="flex min-w-0 items-center gap-2">
          <Table2 className="h-4 w-4 shrink-0 text-[rgb(var(--brand-black-rgb)_/_0.45)]" aria-hidden="true" />
          <span className="min-w-0">
            <span className="block truncate text-[14px] font-medium text-brand-black">
              {planilla.nombre}
            </span>
            <span className="block text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">
              {planilla.hojas.length > 1 ? `${planilla.hojas.length} hojas · ` : ''}
              {filas.length} fila(s) · {celdas} celda(s)
            </span>
          </span>
        </span>

        <div ref={menuRef} className="relative shrink-0">
          <div className="inline-flex overflow-hidden rounded-xl bg-[#F0F2F7]">
            <button
              type="button"
              onClick={() => descargar(planilla.formato)}
              className="inline-flex items-center gap-1.5 py-1.5 px-3 text-[12.5px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.75)] transition-colors hover:bg-[rgb(var(--brand-black-rgb)_/_0.07)]"
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              Descargar .{planilla.formato}
            </button>
            <button
              type="button"
              onClick={() => setMenuAbierto((v) => !v)}
              aria-label="Otro formato"
              aria-haspopup="menu"
              aria-expanded={menuAbierto}
              className="inline-flex items-center border-l border-[rgb(var(--brand-black-rgb)_/_0.08)] py-1.5 px-1.5 text-[rgb(var(--brand-black-rgb)_/_0.6)] transition-colors hover:bg-[rgb(var(--brand-black-rgb)_/_0.07)]"
            >
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
          {menuAbierto && (
            <div
              role="menu"
              className="absolute right-0 top-full z-20 mt-1 w-[230px] rounded-xl border border-[rgb(var(--brand-black-rgb)_/_0.06)] bg-white p-1 shadow-xl"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => descargar(otroFormato)}
                className="block w-full rounded-lg px-2.5 py-2 text-left text-[13px] text-brand-black/90 transition-colors hover:bg-neutral-50"
              >
                Descargar como .{otroFormato}
                {otroFormato === 'csv' && avisoCsv && (
                  <span className="mt-0.5 block text-[11.5px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">
                    Sólo la primera hoja: un CSV es una sola tabla.
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {previa.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[rgb(var(--brand-black-rgb)_/_0.06)]">
          <table className="w-full border-collapse text-[12.5px]">
            <tbody>
              {previa.map((fila, i) => (
                <tr
                  key={i}
                  className={`${i === 0 ? 'bg-[rgb(var(--brand-black-rgb)_/_0.03)] font-medium' : ''} ${
                    i < previa.length - 1 ? 'border-b border-[rgb(var(--brand-black-rgb)_/_0.05)]' : ''
                  }`}
                >
                  {fila.slice(0, COLUMNAS_PREVIA).map((celda, c) => (
                    <td
                      key={c}
                      className="max-w-[180px] truncate px-2.5 py-1.5 align-top text-brand-black/85"
                      title={celda}
                    >
                      {celda}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(ocultas > 0 || planilla.recortadas > 0 || error) && (
        <p className="px-1 pt-2 text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">
          {error ??
            [
              ocultas > 0 ? `+${ocultas} fila(s) más en el archivo.` : '',
              planilla.recortadas > 0
                ? `Se recortaron ${planilla.recortadas} celdas: la planilla era muy grande.`
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
        </p>
      )}
    </div>
  );
};

export default CrearPlanillaCard;
